import type { TcomFormRule, FormConfigOptionsType } from '@x7web/tcom-ui'

interface FormResetParams {
  type?: 'initial' | 'empty'
  fields?: string[]
}
interface ValidateInformation {
  status: 'error' | 'warning' | 'success'
  tips: string
}
type Data = { [key: string]: any }

/**
 * @description 表单重置方法
 * @param {Data} data 表单数据
 * @param {FormResetParams<FormData>} params 重置参数
 */
function formReset(data: Data, params?: FormResetParams) {
  const { type, fields } = params ?? {}
  if (!type || type === 'empty') {
    Object.keys(data).forEach((key) => {
      if (typeof data[key] === 'string') data[key] = ''
      if (typeof data[key] === 'boolean') data[key] = false
      if (Array.isArray(data[key])) data[key] = []
    })
  } else {
    fields?.forEach((key) => {
      if (Array.isArray(data[key])) data[key] = []
      if (typeof data[key] === 'string') data[key] = ''
      if (typeof data[key] === 'boolean') data[key] = false
    })
  }
}

/**
 * @description 给目标字段新的rule规则项目
 * @param {FormRules} rules
 * @param {string[]} fields
 * @param {TcomFormRule} rule
 * @returns {FormRules} 返回新的rules
 */
function formRulesAdd(rules: any, fields: string[], rule: TcomFormRule) {
  const newRules = { ...rules }
  fields.forEach((field) => {
    if (newRules[field]) {
      newRules[field].push(rule)
    } else {
      newRules[field] = [rule]
    }
  })
  return newRules
}

/**
 * @description 给目标表单选项合并新的组件props属性
 * @param {Array<FormConfigOptionsType>} formOptions 表单选项
 * @param {string} propName 目标选项的prop
 * @param {T} compProp 新的组件props属性
 */
function formTargetOptionMergeCompProps<
  T extends Record<string, any> = NonNullable<FormConfigOptionsType['compProps']>
>(formOptions: Array<FormConfigOptionsType>, propName: string, compProp: T) {
  const targetOptionIdx = formOptions.findIndex((option) => option.prop === propName)
  if (targetOptionIdx === -1) return
  Object.assign(<T>formOptions[targetOptionIdx].compProps, compProp)
}

/**
 * @description 设置自定义校验规则
 * @param {Array<FormConfigOptionsType>} options 表单选项
 * @param {string} propName 目标选项的prop
 * @param {ValidateInformation} validateInformation 校验信息
 * @param {boolean} isSetCompStatus 是否设置组件的status
 * @returns {void}
 */
function setSelfValidate<T extends FormConfigOptionsType['compProps']>(
  options: Array<FormConfigOptionsType>,
  propName: string,
  validateInformation: ValidateInformation,
  isSetCompStatus?: boolean
) {
  const { status, tips } = validateInformation
  const idx = options.findIndex((option) => option.prop === propName)
  if (idx === -1) return
  options[idx].formItemProps.status = status
  options[idx].formItemProps.tips = tips
  if (isSetCompStatus) {
    ;(options[idx].compProps as T & { status?: string }).status = status
  }
}

/**
 * @description 清除自定义校验规则
 * @param {Array<FormConfigOptionsType>} options  表单选项
 * @param {string} propName 目标选项的prop
 * @param {boolean} isClearCompStatus 是否设置组件的status
 * @returns {void}
 */
function clearSelfValidate(options: Array<FormConfigOptionsType>, propName: string, isClearCompStatus?: boolean) {
  const idx = options.findIndex((option) => option.prop === propName)
  if (idx === -1) return
  options[idx].formItemProps.status = undefined
  options[idx].formItemProps.tips = ''
  if (isClearCompStatus) {
    ;(options[idx].compProps as { status?: string }).status = undefined
  }
}

export { formReset, formRulesAdd, formTargetOptionMergeCompProps, setSelfValidate, clearSelfValidate }
